// services/DateService.js

import axiosInstance from './axiosInstance';
import { formatShortDateRange } from '../utils/dateUtils';


export async function calculateReadableFortnight(fortnightIndex) {
  try {
    // If fortnightIndex is a Date object or date string, convert it to a relative index
    let queryParam;
    if (fortnightIndex instanceof Date || (typeof fortnightIndex === 'string' && !isNaN(new Date(fortnightIndex).getTime()))) {
      // We need to convert this date to a relative fortnight index instead of sending the raw date
      // Get the current fortnight start date from the server first
      const baseResponse = await axiosInstance.get('/fortnight-start-date/');
      if (!baseResponse.data.fortnights || !Array.isArray(baseResponse.data.fortnights)) {
        throw new Error('Invalid fortnight data structure');
      }
      
      const currentFortnightStart = new Date(baseResponse.data.fortnights[0].start_date);
      const targetDate = new Date(fortnightIndex);
      
      // Calculate number of fortnights between current and target
      const msInFortnight = 14 * 24 * 60 * 60 * 1000; // 14 days in ms
      const relativeIndex = Math.round((targetDate - currentFortnightStart) / msInFortnight);
      queryParam = relativeIndex;
    } else {
      // Use the numeric index directly
      queryParam = fortnightIndex;
    }

    const response = await axiosInstance.get(`/fortnight-start-date/?relative_fortnight_index=${encodeURIComponent(queryParam)}`);

    // Validate response structure
    if (!response.data.fortnights || !Array.isArray(response.data.fortnights)) {
      throw new Error('Invalid fortnight data structure');
    }

    // Validate date format and existence
    const startDate = response.data.fortnights[0]?.start_date;
    if (!startDate || isNaN(new Date(startDate).getTime())) {
      throw new Error('Invalid start date format');
    }

    const fortnightStartDate = new Date(response.data.fortnights[0].start_date); // Accessing the start_date from the first entry in the array
    const fortnightEndDate = new Date(fortnightStartDate);
    fortnightEndDate.setDate(fortnightStartDate.getDate() + 13);

    const formattedRangeFortnight = formatShortDateRange(fortnightStartDate, fortnightEndDate);
    return [fortnightStartDate, formattedRangeFortnight];
  } catch (error) {
    console.error('Error fetching fortnight start date:', error.name);
    throw error;
  }
}

const DateService = {
  getFortnightStartDate: async () => {
    try {
      const response = await axiosInstance.get('/fortnight-start-date/');

      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid fortnight data structure');
      }

      // Validate date format
      if (!response.data.fortnights || !Array.isArray(response.data.fortnights)) {
        throw new Error('Invalid fortnight data structure');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching fortnight start date:', error.name);
      throw error;
    }
  },

  updateFortnightStartDate: async (startDate) => {
    try {
      // Validate input date
      if (!startDate || isNaN(new Date(startDate).getTime())) {
        throw new Error('Invalid start date input');
      }

      // Create a date object and ensure we preserve the date without timezone adjustments
      const dateObj = new Date(startDate);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth();
      const day = dateObj.getDate();
      const utcDate = new Date(Date.UTC(year, month, day));
      
      // Use the UTC date to create the ISO string to avoid timezone issues
      const isoStartDate = utcDate.toISOString().split('T')[0]; // Extract YYYY-MM-DD
      
      const response = await axiosInstance.put(`/fortnight-start-date/${encodeURIComponent(1)}/`, {
        start_date: isoStartDate
      });
      // Validate response
      if (!response.data || !response.data.start_date) {
        throw new Error('Invalid response structure');
      }

      return response.data;
    } catch (error) {
      console.error('Error updating fortnight start date:', error.name);
      throw error;
    }
  },
};

export default DateService;
